import { isMaskedDob } from '@onefootprint/core';
import { getHostedUserTokenOptions, patchHostedUserVault, postHostedOnboardingPrefillVerifiedCommit, postHostedUserVaultDecryptOptions } from '@onefootprint/fetch-hosted';
import type { VaultData } from '@onefootprint/request-types';
import { useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { retryOnConnectionError } from '@/idv2/components/errors/get-error-key';
import { useNavigationOverride } from '@/idv2/features/app-shell';
import { NavigationHeader } from '@/idv2/features/app-shell/navigation-header';
import { CollectData, type CollectDataMode } from '@/idv2/features/collect-data';
import { canShowCompactReview, getCollectDataVaultData } from '@/idv2/features/collect-data/utils/collect-data-helpers';
import { fetchPrefillData } from '@/idv2/features/collect-data/utils/fetch-prefill-data';
import { filterSensitiveDis } from '@/idv2/features/collect-data/utils/filter-sensitive-dis';
import { getCollectDataDis } from '@/idv2/features/collect-data/utils/get-collect-data-dis';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { type FieldError, useVaultMutationError } from '@/idv2/hooks/use-vault-mutation-error';
import { filterBootstrapData } from '@/idv2/utils/filter-bootstrap-data';
import { isOnboardingCompleted } from '@/idv2/utils/is-onboarding-completed';
import { isOnboardingSuperseded } from '@/idv2/utils/is-onboarding-superseded';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn,
  logError
} = getLogger({
  name: 'idv2',
  location: 'collect-data'
});
export const Route = createFileRoute('/onboarding/$token/collect-data')({
  beforeLoad: async ({
    context: {
      currentRequirement,
      queryClient
    },
    params: {
      token
    }
  }) => {
    if (currentRequirement?.kind !== 'collect_data') {
      throw new Error(`Invalid requirement kind: ${currentRequirement?.kind ?? 'undefined'}`);
    }
    const headers = {
      'X-Fp-Authorization': token
    };

    // Fetch token scopes to determine which fields we can decrypt
    const tokenInfo = await queryClient.fetchQuery({
      ...getHostedUserTokenOptions({
        headers
      }),
      retry: retryOnConnectionError
    });

    // Get all fields for the requirement, then filter based on token permissions
    const allFields = getCollectDataDis(currentRequirement);
    const decryptableFields = filterSensitiveDis(allFields, tokenInfo.scopes);
    logInfo('Collect data decrypting vault fields', {
      allFields: allFields.join(','),
      decryptableFields: decryptableFields.join(','),
      scopes: tokenInfo.scopes.join(',')
    });
    const fullVault = await queryClient.fetchQuery({
      ...postHostedUserVaultDecryptOptions({
        headers,
        body: {
          fields: decryptableFields
        }
      }),
      retry: retryOnConnectionError
    });
    return {
      currentRequirement,
      fullVault
    };
  },
  loader: async ({
    params: {
      token
    },
    context: {
      queryClient,
      currentRequirement,
      config,
      fullVault,
      kycBootstrapData
    }
  }) => {
    // Fetch prefill data (Footprint prefill takes precedence over Verified prefill)
    const {
      allVaultData,
      verifiedPrefillResponse,
      footprintPrefillContext,
      footprintPrefillChallengeData
    } = await fetchPrefillData({
      queryClient,
      token,
      currentRequirement,
      fullVault,
      config
    });

    // Filter to only the fields needed for collect-data
    const decryptedVault = getCollectDataVaultData(currentRequirement, allVaultData);

    // Get allowed fields and filter bootstrap data to only include fields collected by the playbook
    const allowedFields = getCollectDataDis(currentRequirement);
    const {
      filteredData: filteredBootstrapData,
      bootstrapFields
    } = filterBootstrapData(kycBootstrapData, allowedFields);

    // Merge filtered bootstrap data with decrypted vault (bootstrap takes precedence)
    const mergedVault: VaultData = {
      ...decryptedVault,
      ...filteredBootstrapData
    };
    const manualEntryVault: VaultData = {
      ...getCollectDataVaultData(currentRequirement, fullVault),
      ...filteredBootstrapData
    };
    logInfo('Collect data filtered vault', {
      screenFields: Object.keys(decryptedVault).join(','),
      bootstrapFields: bootstrapFields.join(',')
    });
    return {
      decryptedVault: mergedVault,
      manualEntryVault,
      currentRequirement,
      verifiedPrefillResponse,
      footprintPrefillContext,
      footprintPrefillChallengeData,
      bootstrapFields
    };
  },
  component: CollectDataPage
});
function CollectDataPage() {
  const loaderData = Route.useLoaderData();
  const {
    currentRequirement,
    footprintPrefillContext,
    footprintPrefillChallengeData,
    bootstrapFields
  } = loaderData;
  const {
    token
  } = Route.useParams();
  const footprint = useFootprint();
  const {
    l10n,
    config
  } = Route.useRouteContext();
  const {
    t
  } = useTranslation('idv2');
  const {
    toast
  } = useToast();
  const handleVaultError = useVaultMutationError();
  const disableSsnValidation = !config.isLive;

  // A stale prefill fails the commit on every retry (any other write to the vault makes it stale),
  // so any commit failure drops the Verified data and the user enters their details instead.
  const [isVerifiedPrefillDropped, setIsVerifiedPrefillDropped] = useState(false);
  const decryptedVault = isVerifiedPrefillDropped ? loaderData.manualEntryVault : loaderData.decryptedVault;
  const verifiedPrefillResponse = isVerifiedPrefillDropped ? undefined : loaderData.verifiedPrefillResponse;

  // Compact review is a mode of this screen; the route owns the mode + shell nav override so the
  // feature component stays presentational (matches transfer / collect-document / business-owners).
  const hasOverlay = Boolean(footprintPrefillContext) || verifiedPrefillResponse?.result?.kind === 'step_up';
  const canShowReview = canShowCompactReview(currentRequirement, decryptedVault, hasOverlay);
  const [mode, setMode] = useState<CollectDataMode>(canShowReview ? 'review' : 'edit');

  // While editing from the review, take over the shell's close button so it returns to review.
  const {
    setNavigationOverridden
  } = useNavigationOverride();
  const isEditingFromReview = canShowReview && mode === 'edit';
  useEffect(() => {
    if (!isEditingFromReview) return;
    setNavigationOverridden(true);
    return () => setNavigationOverridden(false);
  }, [isEditingFromReview, setNavigationOverridden]);
  const headers: Record<string, string> = {
    'X-Fp-Authorization': token
  };
  // Add bootstrap fields header if there are bootstrapped fields
  if (bootstrapFields.length > 0) {
    headers['X-Fp-Bootstrapped-Fields'] = bootstrapFields.join(',');
  }
  const {
    mutateAsync: commitVerifiedPrefill,
    isPending: isCommitPending
  } = useMutation({
    mutationFn: (prefillId: string) => postHostedOnboardingPrefillVerifiedCommit({
      headers,
      body: {
        prefillId
      }
    })
  });
  const {
    mutateAsync: vaultMutation,
    isPending: isVaultMutationPending
  } = useMutation({
    mutationFn: (data: VaultData) => patchHostedUserVault({
      headers,
      body: data
    })
  });
  // The save finishes before the next step loads, and a second submit in between would save twice.
  const [isNavigating, setIsNavigating] = useState(false);
  const onSubmit = async (data_0: VaultData, onError: (errors: FieldError[]) => void) => {
    logInfo('Form submission started', {
      vaultKeys: Object.keys(data_0).join(',')
    });
    if (verifiedPrefillResponse?.result?.kind === 'located') {
      try {
        // If we have staged prefill information from Verified, commit it before we apply any writes on top
        await commitVerifiedPrefill(verifiedPrefillResponse.result.id);
      } catch (error) {
        // The root boundary is already routing this to the terminal screen.
        if (isOnboardingSuperseded(error)) return;
        logWarn('Verified prefill commit failed, falling back to manual entry', error);
        setIsVerifiedPrefillDropped(true);
        setMode('edit');
        toast({
          title: t($ => $.collectData.pages.verifiedPrefill.manualEntryToast.title),
          description: t($_0 => $_0.collectData.pages.verifiedPrefill.manualEntryToast.description),
          variant: 'error'
        });
        return;
      }
    }
    try {
      await vaultMutation(data_0);
      logInfo('Vault mutation succeeded');
      setIsNavigating(true);
      await footprint.navigation.forward();
    } catch (error_0) {
      // Another tab finished this workflow. Proceeding returns its validation token, so the user
      // lands on completion instead of retrying a write that can't succeed.
      if (isOnboardingCompleted(error_0)) {
        logInfo('Workflow already completed, proceeding');
        setIsNavigating(true);
        await footprint.navigation.forward();
        return;
      }
      const fieldErrors = handleVaultError(error_0, onError);
      const log = fieldErrors.length > 0 ? logWarn : logError;
      log('Vault mutation failed', error_0, {
        fieldErrorCount: fieldErrors.length
      });
    } finally {
      setIsNavigating(false);
    }
  };

  // In some situations, the backend returns a scrubbed SSN9 that only includes the last 4 of the SSN.
  const ssn9DigitCount = (decryptedVault['id.ssn9']?.match(/\d/g) || []).length;
  const scrubbedSsn9Value = ssn9DigitCount === 4 ? decryptedVault['id.ssn9'] : undefined;
  // Likewise, a Verified-prefill DOB comes back with its year masked (`••••-MM-DD`).
  const scrubbedDobValue = isMaskedDob(decryptedVault['id.dob']) ? decryptedVault['id.dob'] : undefined;
  const onPrefill = async () => {
    logInfo('Prefill completed, navigating forward');
    await footprint.navigation.forward({
      confirmations: 'wait'
    });
  };
  return <>
      {isEditingFromReview && <NavigationHeader canGoBack onBack={() => setMode('review')} />}
      <CollectData
    // useForm reads its default values only on mount, so remount to clear the dropped Verified data.
    key={isVerifiedPrefillDropped ? 'manual-entry' : 'initial'} l10n={l10n} isLoading={isCommitPending || isVaultMutationPending || isNavigating} onSubmit={onSubmit} authToken={token} decryptedVault={decryptedVault} currentRequirement={currentRequirement} disableSsnValidation={disableSsnValidation} verifiedPrefillResponse={verifiedPrefillResponse} scrubbedSsn9Value={scrubbedSsn9Value} scrubbedDobValue={scrubbedDobValue} onPrefill={onPrefill} footprintPrefillContext={footprintPrefillContext} footprintPrefillChallengeData={footprintPrefillChallengeData} canShowReview={canShowReview} mode={mode} onModeChange={setMode} />
    </>;
}